import React from 'react';
import { api, formatINR } from '../../../lib/store';
import { formatSizeInput } from '../../../lib/utils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Badge } from '../../ui/badge';
import { Separator } from '../../ui/separator';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, ChevronLeft, ChevronRight, Image as ImageIcon, X, Wrench, Upload, Package, Zap, Download } from 'lucide-react';
import { logger } from '../../../lib/logger';
import {
  CATEGORIES,
  RATE_TYPES,
  THICKNESS_BULK_CATEGORIES,
  WEIGHT_BULK_CATEGORIES,
  PLYWOOD_THICKNESSES,
  BLOCKBOARD_THICKNESSES,
  MDF_THICKNESSES,
  DOORS_THICKNESSES,
  WPCPVC_THICKNESSES,
  ACP_THICKNESSES,
  ADHESIVE_WEIGHTS,
  PLYWOOD_SIZES,
  DOOR_SIZES,
  DOOR_SIZES_WITH_SQFT,
  emptyProduct,
} from '../constants';

// Extracted from ProductManagement.js (R2). Receives parent state/handlers as props.
export function CategoryManageDialog(props) {
  const {
    cancelDelete,
    categoryPassword,
    categoryToDelete,
    customCategories,
    deleteConfirmStep,
    deleteCustomCategory,
    newCategoryName,
    saveCustomCategory,
    setCategoryPassword,
    setCategoryToDelete,
    setDeleteConfirmStep,
    setNewCategoryName,
    setShowCategoryDialog,
    showCategoryDialog,
  } = props;
  return (
    <>
      {/* Add Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={(open) => {
        setShowCategoryDialog(open);
        if (!open) {
          setNewCategoryName('');
          setCategoryPassword('');
          setCategoryToDelete(null);
          setDeleteConfirmStep(0);
        }
      }}>
        <DialogContent className="max-w-sm" data-testid="add-category-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#1A1C16]">Manage Categories</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C6150]">New Category Name</Label>
              <Input
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="e.g., Glass, Tiles, Flooring"
                className="mt-1 bg-[#F9FAF7] border-[#E2E6D9]"
                data-testid="new-category-input"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-[#5C6150]">Password (Required)</Label>
              <Input
                type="password"
                value={categoryPassword}
                onChange={(e) => setCategoryPassword(e.target.value)}
                placeholder="Enter password"
                className="mt-1 bg-[#F9FAF7] border-[#E2E6D9]"
                onKeyDown={(e) => e.key === 'Enter' && saveCustomCategory()}
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowCategoryDialog(false); setNewCategoryName(''); setCategoryPassword(''); }}
                className="flex-1 border-[#E2E6D9]"
              >
                Cancel
              </Button>
              <Button
                onClick={saveCustomCategory}
                className="flex-1 bg-[#556B2F] hover:bg-[#465926] text-white"
                data-testid="save-category-btn"
              >
                Add Category
              </Button>
            </div>
            
            {/* Custom categories list with delete */}
            {customCategories.length > 0 && (
              <div className="pt-3 border-t border-[#E2E6D9]">
                <p className="text-xs text-[#8F9485] mb-2">Custom categories added:</p>
                <div className="space-y-2">
                  {customCategories.map(cat => (
                    <div key={cat} className="flex items-center justify-between bg-[#F9FAF7] px-3 py-2 rounded-md">
                      <span className="text-sm">{cat}</span>
                      {categoryToDelete === cat ? (
                        <div className="flex items-center gap-2">
                          {deleteConfirmStep === 1 && (
                            <>
                              <span className="text-xs text-red-600">Delete?</span>
                              <Button size="sm" variant="destructive" className="h-6 text-xs" onClick={() => deleteCustomCategory(cat)}>Yes</Button>
                              <Button size="sm" variant="outline" className="h-6 text-xs" onClick={cancelDelete}>No</Button>
                            </>
                          )}
                          {deleteConfirmStep === 2 && (
                            <>
                              <Input
                                type="password"
                                value={categoryPassword}
                                onChange={(e) => setCategoryPassword(e.target.value)}
                                placeholder="Password"
                                className="w-24 h-7 text-xs"
                                onKeyDown={(e) => e.key === 'Enter' && deleteCustomCategory(cat)}
                              />
                              <Button size="sm" variant="destructive" className="h-6 text-xs" onClick={() => deleteCustomCategory(cat)}>Confirm</Button>
                              <Button size="sm" variant="outline" className="h-6 text-xs" onClick={cancelDelete}>Cancel</Button>
                            </>
                          )}
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => deleteCustomCategory(cat)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
